var _slicedToArray = function () { function sliceIterator(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"]) _i["return"](); } finally { if (_d) throw _e; } } return _arr; } return function (arr, i) { if (Array.isArray(arr)) { return arr; } else if (Symbol.iterator in Object(arr)) { return sliceIterator(arr, i); } else { throw new TypeError("Invalid attempt to destructure non-iterable instance"); } }; }();

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

import log from './log';
import { createBrowserHistory } from 'history';
import { empty, merge, concat, of, asyncScheduler, Observable } from 'rxjs';
import { followLink$, jumpBack$, followAnchor$ } from './intents';
import { fromJS, Set, List, OrderedMap } from 'immutable';
import { matchPath } from 'react-router';
import { parse, stringify } from 'query-string';
import { unary, find, type, path } from 'ramda';
import { filter, map, observeOn, publishReplay, refCount, skip, switchMap, tap } from 'rxjs/operators';

var preventNavigationCallback = null;
var continueNavigationCallback = null;

export var preventNavigation = function preventNavigation(callback) {
  preventNavigationCallback = callback;
  return function () {
    preventNavigationCallback = null;
    continueNavigationCallback = null;
  };
};

export var history = createBrowserHistory({
  getUserConfirmation: function getUserConfirmation(_, historyCallback) {
    if (preventNavigationCallback) {
      preventNavigationCallback(function (shouldContinue) {
        if (shouldContinue) {
          continueNavigationCallback ? continueNavigationCallback() : historyCallback(shouldContinue);
        }
      });
    } else {
      return false;
    }
  },
  basename: process.env.REACT_APP_ROUTER_BASE_URL || process.env.PUBLIC_URL || ''
});

var programmaticNavigationTokens = Set();
var currentAction = void 0;

followLink$.subscribe(function (location) {
  return history.push(location);
});
jumpBack$.subscribe(function () {
  return history.goBack();
});

var startLocation$ = of(history.location).pipe(observeOn(asyncScheduler), tap(log('Location at start')), publishReplay(1), refCount());

var historyLocation$ = Observable.create(function (observer) {
  history.listen(function (location) {
    observer.next(location);
    currentAction = null;
  });
}).pipe(tap(log('Location from history.listen')));

export function createLocation$(pathToIntent) {
  return merge(createLocationHandler$(pathToIntent), startLocation$.pipe(filter(hasNoHandlers(pathToIntent))), historyLocation$).pipe(map(completeState), map(completeQuery), map(unary(fromJS)), tap(log('Location broadcasted')), publishReplay(1), refCount());
}

export function createNavigateTo$(pathname) {
  var state = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var query = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var action = arguments[3];
  var hash = arguments[4];

  if (type(query) === 'String') query = parse(query);
  var navToken = generateNavToken();
  programmaticNavigationTokens = programmaticNavigationTokens.add(navToken);

  return of({
    pathname: pathname,
    state: _extends({}, state, { navToken: navToken }),
    query: query,
    search: stringify(query),
    hash: hash
  }).pipe(tap(log('Programmatic nav ' + pathname + ' ' + (action || ''))), tap(function (location) {
    if (action === undefined) action = currentAction;
    if (!action || action === 'PUSH') {
      history.push(location);
    } else {
      history.replace(location);
    }
  }), tap(function () {
    programmaticNavigationTokens = programmaticNavigationTokens.delete(navToken);
  }), skip());
}

export var createRedirectTo$ = function createRedirectTo$(pathname) {
  var state = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var query = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return createNavigateTo$(pathname, state, query, 'REPLACE');
};

export var anchorLocation$ = followAnchor$.pipe(switchMap(function (hash) {
  var navToken = generateNavToken();
  programmaticNavigationTokens = programmaticNavigationTokens.add(navToken);
  return of(fromJS(history.location).set('hash', '#' + hash.replace(/^#/, '')).updateIn(['state', 'navToken'], function () {
    return navToken;
  })).pipe(tap(log('New location from anchor navigation')), tap(function (location) {
    return history.push(location.toJS());
  }), tap(function () {
    return programmaticNavigationTokens.delete(navToken);
  }), skip());
}));

function createLocationHandler$(pathToIntent) {
  return concat(startLocation$.pipe(switchMap(toMaybeHandlerAndParams$)), Observable.create(handlerForManagedPaths)).pipe(tap(log('Handling location from history block')), tap(runHandler), skip());

  function toMaybeHandlerAndParams$(location) {
    var handlerAndParams = locationToHandlerAndParams(location, pathToIntent);
    return handlerAndParams ? of(handlerAndParams).pipe(tap(log('Initial location handler'))) : empty();
  }

  function handlerForManagedPaths(observer) {
    return history.block(blockManagedPaths(observer, pathToIntent));
  }
}

function locationToHandlerAndParams(location, pathToIntent) {
  var search = location.search,
      hash = location.hash;

  var route = void 0;
  var match = find(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 1),
        path = _ref2[0];

    route = matchPath(location.pathname, { path: path, exact: true });
    return !!route;
  }, List(OrderedMap(pathToIntent)));

  if (!match) return null;
  return { params: route.params, handler: match[1], search: search, hash: hash };
}

function blockManagedPaths(observer, pathToIntent) {
  return function testBlock(location, action) {
    if (isProgrammaticNavigation(location)) return undefined;
    var handlerAndParams = locationToHandlerAndParams(location, pathToIntent);
    if (preventNavigationCallback) {
      continueNavigationCallback = handlerAndParams ? function () {
        currentAction = action;
        handlerAndParams.action = action;
        observer.next(handlerAndParams);
      } : null;
      return 'blocked';
    }
    if (!handlerAndParams) return undefined;
    currentAction = action;
    handlerAndParams.action = action;
    observer.next(handlerAndParams);
    return 'blocked';
  };
}

function isProgrammaticNavigation(newLocation) {
  var navToken = path(['state', 'navToken'], newLocation);
  var isProgrammatic = programmaticNavigationTokens.has(navToken);
  return isProgrammatic;
}

function runHandler(_ref3) {
  var handler = _ref3.handler,
      params = _ref3.params,
      search = _ref3.search,
      action = _ref3.action,
      hash = _ref3.hash;

  handler(params, search, action, hash);
}

function hasNoHandlers(pathToIntent) {
  return function filter(location) {
    var isManaged = !!locationToHandlerAndParams(location, pathToIntent);
    return !isManaged;
  };
}

function completeState(location) {
  if (!location.state) location.state = {};
  return location;
}

function completeQuery(location) {
  if (!location.query) location.query = parse(location.search || '');
  return location;
}

function generateNavToken() {
  return (Date.now() + Math.random() * 0xfffff).toString(36).toUpperCase();
}